import { Alert, Box, Button, Group, Loader, Text } from '@mantine/core'
import { IconX } from '@tabler/icons-react'
import { Terminal } from '@xterm/xterm'
import { FitAddon } from '@xterm/addon-fit'
import '@xterm/xterm/css/xterm.css'
import { useEffect, useRef, useState } from 'react'

/**
 * Reproduz uma gravação de sessão num terminal.
 *
 * Usa o mesmo xterm da sessão viva, e de propósito: o conteúdo gravado tem as
 * sequências de escape do shell remoto, então qualquer outro renderizador
 * mostraria `\x1b[0m` no meio do texto. O terminal é o único que sabe pintar.
 *
 * `disableStdin` porque isto é uma gravação: aceitar tecla daria a impressão de
 * que dá para interagir com uma sessão que terminou.
 */
export function ReprodutorDeSessao({
  carregar,
  onFechar,
}: {
  carregar: () => Promise<Uint8Array>
  onFechar: () => void
}) {
  const caixa = useRef<HTMLDivElement>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [carregando, setCarregando] = useState(true)

  useEffect(() => {
    let vivo = true
    let term: Terminal | null = null

    void (async () => {
      let dados: Uint8Array
      try {
        dados = await carregar()
      } catch (e) {
        if (vivo) {
          setErro(
            e instanceof Error && e.message.includes('404')
              ? 'A gravação não está mais disponível: passou dos 90 dias de retenção e o conteúdo foi apagado. O registro da sessão continua na trilha.'
              : 'Não consegui carregar a gravação.',
          )
          setCarregando(false)
        }
        return
      }
      if (!vivo || !caixa.current) return

      term = new Terminal({
        fontFamily: '"JetBrains Mono", "Fira Code", monospace',
        fontSize: 13,
        theme: { background: '#011e28', foreground: '#e6f3f5', cursor: '#098d93' },
        disableStdin: true,
        cursorBlink: false,
        scrollback: 50_000,
      })
      const fit = new FitAddon()
      term.loadAddon(fit)
      term.open(caixa.current)
      fit.fit()
      term.write(dados)
      setCarregando(false)
    })()

    return () => {
      vivo = false
      term?.dispose()
    }
  }, [carregar])

  return (
    <Box>
      <Group justify="space-between" mb={4}>
        <Text size="xs" c="dimmed">
          Gravação da sessão — somente leitura
        </Text>
        <Button size="xs" variant="subtle" leftSection={<IconX size={14} />} onClick={onFechar}>
          Fechar
        </Button>
      </Group>
      {erro ? (
        <Alert color="orange" p="xs"><Text size="sm">{erro}</Text></Alert>
      ) : (
        <Box style={{ position: 'relative', minHeight: 240 }}>
          {carregando && (
            <Group justify="center" p="lg"><Loader size="sm" /></Group>
          )}
          <div ref={caixa} style={{ height: 360 }} />
        </Box>
      )}
    </Box>
  )
}
